import { bbox as turfBbox, buffer } from '@turf/turf';
import type { Map as MlMap } from 'maplibre-gl';
import type { Feature, Polygon } from 'geojson';
import type { Boundary } from '../../shared/protocol';
import type { MapFeature } from '../../shared/nearestFeature';

/**
 * v24 BOLT-118 — the map-feature harvest, on top of v23's tile-feature layer.
 *
 * Everything comes from the vector tiles we ALREADY render (the same
 * `querySourceFeatures` pattern portal placement and Home Turf use). No
 * Overpass, no new data source, no extra network.
 *
 * PUBLIC DATA: these are things drawn on the map. The privacy invariant is only
 * ever about the hider's coordinates, which do not move.
 */

/** Each card's tag maps to the OMT source-layers + filters that find it. */
interface TagSpec {
  layers: string[];
  /** True when a tile feature belongs to this tag. */
  match: (props: Record<string, unknown>, layer: string) => boolean;
}

const SPECS: Record<string, TagSpec> = {
  // leisure=park
  // `poi` first: at play zoom the OMT `park` layer is empty and real parks
  // arrive as poi points (verified against the default play area's tiles).
  park: {
    layers: ['poi', 'park', 'landuse'],
    match: (p) => ['park', 'nature_reserve', 'dog_park', 'garden'].includes(String(p.class ?? p.subclass ?? '')),
  },
  // amenity=restaurant + fast_food + cafe, merged into ONE "dining" set: each
  // place stays its own site, but all three tags contribute (BOLT-119).
  dining: { layers: ['poi'], match: (p) => ['restaurant', 'fast_food', 'cafe'].includes(String(p.subclass ?? '')) },
  playground: { layers: ['poi', 'landuse'], match: (p) => String(p.subclass ?? p.class ?? '') === 'playground' },
  school: { layers: ['poi', 'landuse'], match: (p) => ['school', 'kindergarten', 'college', 'university'].includes(String(p.subclass ?? p.class ?? '')) },
  // natural=water (areas) + waterway=river/stream (lines): one "water" set of
  // DISTINCT sites, never one fused blob (BOLT-122).
  water: {
    layers: ['water', 'waterway'],
    match: (p, layer) => layer === 'water' || ['river', 'stream', 'canal'].includes(String(p.class ?? '')),
  },
  // bridge=yes rides the transportation layer's brunnel property (BOLT-121).
  bridge: { layers: ['transportation'], match: (p) => String(p.brunnel ?? '') === 'bridge' },
};

export const LANDMARK_TAGS = Object.keys(SPECS);

/**
 * How far PAST the boundary to look. A park just outside the edge can genuinely
 * be nearest to an edge hider, and ignoring it would mis-assign the edge cells,
 * so the SITES may be anywhere; only the CELLS stop at the play area.
 */
export const FEATURE_BUFFER_M = 500;

/** Are the vector tiles actually loaded? `querySourceFeatures` only ever sees
 *  what is currently loaded, so this is the difference between "none here" and
 *  "not yet". */
export function tilesReady(map: MlMap): boolean {
  try {
    return map.isSourceLoaded?.('omt') === true || map.areTilesLoaded?.() === true;
  } catch {
    return false;
  }
}

export function bufferedBoundary(boundary: Boundary): Feature<Polygon> {
  try {
    return (buffer(boundary, FEATURE_BUFFER_M / 1000, { units: 'kilometers' }) ?? boundary) as Feature<Polygon>;
  } catch {
    return boundary as Feature<Polygon>;
  }
}

function inBox(geometry: GeoJSON.Geometry, box: number[]): boolean {
  try {
    const [w, s, e, n] = turfBbox({ type: 'Feature', properties: {}, geometry } as Feature);
    return !(e < box[0] || box[2] < w || n < box[1] || box[3] < s);
  } catch {
    return false;
  }
}

/**
 * Harvest one tag's features over the play area PLUS the buffer.
 *
 * `poi` caveat: point features only exist at z14+, so a zoomed-out map simply
 * yields fewer of them. That is not a failure mode we can fix here; it is what
 * the data-requirement rule (a card dealt disabled with a reason) exists for.
 */
export function harvestFeatures(map: MlMap, tag: string, boundary: Boundary): MapFeature[] {
  const spec = SPECS[tag];
  if (!spec) return [];
  if (!map.getSource?.('omt')) return syntheticFeatures(tag, boundary);
  // v24 field fix: an empty harvest against tiles that have not arrived looks
  // exactly like "this neighbourhood has no parks", and acting on that guess
  // disables a good card for the rest of the game. Report "not ready" instead,
  // and let the caller retry.
  if (!tilesReady(map)) return [];
  const box = turfBbox(bufferedBoundary(boundary));
  const out: MapFeature[] = [];
  const seen = new Set<string>();
  for (const layer of spec.layers) {
    let feats: GeoJSON.Feature[] = [];
    try {
      feats = map.querySourceFeatures('omt', { sourceLayer: layer });
    } catch {
      continue;
    }
    for (const f of feats) {
      const g = f.geometry;
      if (!g) continue;
      const props = (f.properties ?? {}) as Record<string, unknown>;
      if (!spec.match(props, layer)) continue;
      if (!inBox(g, box as number[])) continue;
      // Tiles repeat features across their edges; dedupe by geometry.
      const key = JSON.stringify(g);
      if (seen.has(key)) continue;
      seen.add(key);
      const name = typeof props.name === 'string' ? props.name : undefined;
      out.push({ id: `${tag}-${out.length}-${key.length}`, name, geometry: g as MapFeature['geometry'] });
    }
  }
  return out;
}

/**
 * The keyless/CI twin (à la `syntheticGrid` and `syntheticBarriers`): the
 * fallback style has no vector tiles at all, and the contract since v9 is that
 * every flow stays exercisable without a MapTiler key. Three well-separated
 * features per tag, so the partition, the preview, and the cut all run.
 */
export function syntheticFeatures(tag: string, boundary: Boundary): MapFeature[] {
  const [w, s, e, n] = turfBbox(boundary) as number[];
  const dx = (e - w) / 4;
  const dy = (n - s) / 4;
  const spots: [number, number][] = [
    [w + dx, s + dy],
    [e - dx, s + dy],
    [w + 2 * dx, n - dy],
  ];
  // Bridges are lines; dining is points; everything else is an area, so the
  // synthetic set exercises each geometry KIND the real data would produce.
  return spots.map(([lng, lat], i) => {
    const id = `${tag}-synthetic-${i}`;
    const name = i === 0 ? `Test ${tag} ${i + 1}` : undefined;
    if (tag === 'dining') return { id, name, geometry: { type: 'Point' as const, coordinates: [lng, lat] } };
    if (tag === 'bridge') {
      return { id, name, geometry: { type: 'LineString' as const, coordinates: [[lng - dx / 6, lat], [lng + dx / 6, lat]] } };
    }
    const h = Math.min(dx, dy) / 6;
    return {
      id,
      name,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [[[lng - h, lat - h], [lng + h, lat - h], [lng + h, lat + h], [lng - h, lat + h], [lng - h, lat - h]]],
      },
    };
  });
}
